//import React, { useState, useEffect, useRef } from 'react';
import { CartContext } from '@/apps/providers/CartContext';
import { useUser } from '@/apps/providers/UserContext';
import { useAnalytics } from './useAnalytics';
import { useChat } from './useChat';
import useTranslations from '@/apps/providers/useTranslations';
import ChatMessage from './ChatMessage';
import ChatSuggestions from './ChatSuggestions';
import ChatInput from './ChatInput';
import ChatHeader from './ChatHeader';
import { chatApi } from '@/apps/lib/chatApi';

/**
 * ChatWidget - Main chat interface component with personalization
 * Integrates with UserContext for personalized recommendations
 * Integrates with CartContext to add items to cart
 * Tracks all analytics events for optimization
 */
function ChatWidget(props) {
  const { startOpen, inLanding } = props;

  const { isOpen, setIsOpen, sessionId, setSessionId, business, menu, addItemToCart } = useChat();
  const { user, isAuthenticated, getDisplayName, getPersonalizedRecommendations, orderHistory, favoriteItems } = useUser();
  const analytics = useAnalytics();
  const ANALYTICS_EVENTS = analytics.ANALYTICS_EVENTS;
  const { tr, trmap } = useTranslations();

  const [messages, setMessages] = React.useState([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const [suggestions, setSuggestions] = React.useState([]);
  const [currentProduct, setCurrentProduct] = React.useState(null);
  const [followUp, setFollowUp] = React.useState(null);
  const [sessionStartTime, setSessionStartTime] = React.useState(null);
  const [messageCount, setMessageCount] = React.useState(0);
  const [configurationStartTime, setConfigurationStartTime] = React.useState(null);
  const messagesEndRef = React.useRef(null);
  const inputRef = React.useRef(null);

  // Initialize chat session when widget opens
  React.useEffect(() => {

    if (business && menu && isOpen ) {
      initializeChat();
    } else {
      console.log("not initializing", business , menu , isOpen ,sessionId)
    }
  }, [isOpen, menu, business, sessionId]);

  // Scroll to bottom when messages change
  React.useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Auto-focus input when chat opens
  React.useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  React.useEffect(() => {
     setIsOpen(startOpen)
  }, [startOpen]);

  const initializeChat = async () => {

    console.log("initializeChat with", business)
    const startTime = performance.now();
    setIsLoading(true);

    try {
      const response = await chatApi.init({
        businessId: business?.id,
        locationId: menu?.locationId || menu?.location_id,
        menuId: menu?.id,
        userId: user?.id || 0,
        sessionId: sessionId
      });

      console.log("initializeChat with", response)

      if (response.success && response.data) {
        setSessionId(response.data.sessionId);
        setSessionStartTime(new Date());

        // Track session started
        const isNewSession = true;
        analytics.trackSessionStarted(isNewSession, response.data.sessionId, isAuthenticated());

        // Track widget load performance
        analytics.trackPerformance('widget_load', performance.now() - startTime);

        // Generate personalized greeting
        let greetingMessage = response.data.message || tr('chat.greeting.default');

        // If user is logged in and has order history, show personalized greeting
        if (isAuthenticated() && orderHistory.length > 0) {
          greetingMessage = trmap('chat.greeting.welcome_back', { NAME: getDisplayName() });

          analytics.trackEvent('personalized_greeting_shown', {
            order_count: orderHistory.length,
            has_favorites: favoriteItems.length > 0,
          });
        }

        // Add initial greeting message
        const greeting = {
          role: 'assistant',
          content: greetingMessage,
          timestamp: new Date(),
        };

        setMessages([greeting]);

        // Generate personalized suggestions with key/value structure
        let personalizedSuggestions = [
          { key: 'chat.suggestions.show_menu', value: tr('chat.suggestions.show_menu') },
          { key: 'chat.suggestions.recommend', value: tr('chat.suggestions.recommend') },
          { key: 'chat.suggestions.cheap', value: tr('chat.suggestions.cheap') },
        ];

        // If logged in with favorites, add quick reorder options
        if (isAuthenticated() && favoriteItems.length > 0) {
          personalizedSuggestions = [
            { key: 'chat.suggestions.order_again', value: trmap('chat.suggestions.order_again', { NAME: favoriteItems[0].name }) },
            { key: 'chat.suggestions.recommend', value: tr('chat.suggestions.recommend') },
            { key: 'chat.suggestions.show_menu', value: tr('chat.suggestions.show_menu') },
          ];
        }

        setSuggestions(personalizedSuggestions);

        // Track funnel step: chat_ready
        analytics.trackFunnelStep('chat_ordering', 'chat_ready', true);
      } else if (response.success && response.message) {
        // the reponse may include "Multiple locations"

      }
    } catch (error) {
      console.error('Failed to initialize chat:', error);
      analytics.trackError('api_init_failed', error.message);
      addErrorMessage(tr('chat.error.init_failed'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendMessage = async (messageText) => {
    if (!messageText.trim() || isLoading) return;

    // Track first message time
    if (messageCount === 0 && sessionStartTime) {
      const timeToFirstMessage = new Date() - sessionStartTime;
      analytics.trackPerformance('first_message_time', timeToFirstMessage);
    }

    // Add user message
    const userMessage = {
      role: 'user',
      content: messageText,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setMessageCount((prev) => prev + 1);
    setIsLoading(true);
    setFollowUp(null);

    const apiStartTime = performance.now();

    try {
      console.log("send message with", sessionId, messageText)

      const response = await chatApi.sendMessage(sessionId, messageText);

      // Track API response time
      analytics.trackPerformance('api_response_time', performance.now() - apiStartTime);

      if (response.success && response.data) {
        const { data } = response;

        // Track message sent
        analytics.trackMessageSent(messageText, data.intent);

        // Track intent
        if (data.intent) {
          analytics.trackEvent(`intent_${data.intent}`, {
            message_length: messageText.length,
            has_products: (data.products?.length || 0) > 0,
          });
        }

        // Add assistant message
        const assistantMessage = {
          role: 'assistant',
          content: data.message,
          timestamp: new Date(),
          products: data.products,
          followUp: data.follow_up,
        };

        setMessages((prev) => [...prev, assistantMessage]);

        // Track message received
        analytics.trackMessageReceived(
          data.message,
          data.intent,
          (data.products?.length || 0) > 0,
          !!data.follow_up
        );

        // Track product views
        if (data.products && data.products.length > 0) {
          data.products.forEach((product) => {
            analytics.trackProductViewed(product, 'search');
          });
        }

        // Track funnel step: message_exchange
        analytics.trackFunnelStep('chat_ordering', 'message_exchange', true);

        // Handle cart actions
        if (data.cart_action && data.cart_action.action === 'add') {
          handleCartAction(data.cart_action);
        }

        // Track configuration started
        if (data.follow_up && data.follow_up.prompt_type === 'size') {
          const product = findProductById(data.follow_up.product_id, data.products);
          if (product) {
            setCurrentProduct(product);
            setConfigurationStartTime(new Date());
            analytics.trackConfigurationStarted(product);
          }
        }

        // Update follow-up prompt
        if (data.follow_up) {
          setFollowUp(data.followUp);
        }

        // Update suggestions - process API suggestions into key/value objects
        if (data.suggestions && data.suggestions.length > 0) {
          const processedSuggestions = data.suggestions.map((s) => ({
            key: s,
            value: s,
          }));
          setSuggestions(processedSuggestions);
        }
      }
    } catch (error) {
      console.error('Failed to send message:', error);
      analytics.trackError('api_message_failed', error.message);
      addErrorMessage(tr('chat.error.message_failed'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleCartAction = (cartAction) => {
    try {
      if (cartAction.action === 'add' && cartAction.product) {
        // Convert backend cart item to frontend format
        const cartItem = {
          ...cartAction.product,
          quantity: cartAction.product.quantity || 1,
          menuId: menu?.id,
        };

        addItemToCart(cartItem, { quantity: 1 });

        // Track cart add via chat
        analytics.trackCartAddViaChat(cartItem, 'chat');

        // Track configuration completed if applicable
        if (currentProduct && configurationStartTime) {
          const duration = (new Date() - configurationStartTime) / 1000;
          analytics.trackConfigurationCompleted(currentProduct, duration, messageCount);
          setConfigurationStartTime(null);
          setCurrentProduct(null);
        }

        // Track funnel step: cart_add
        analytics.trackFunnelStep('chat_ordering', 'cart_add', true);
      }
    } catch (error) {
      console.error('Failed to add to cart:', error);
      analytics.trackError('cart_add_failed', error.message, {
        product_id: cartAction.product?.id,
      });
    }
  };

  const handleSuggestionClick = (suggestion) => {
    // Handle both string (legacy) and object (new) suggestions
    const suggestionText = typeof suggestion === 'object' ? suggestion.value : suggestion;
    const suggestionKey = typeof suggestion === 'object' ? suggestion.key : suggestion;

    analytics.trackEvent(ANALYTICS_EVENTS.MESSAGE_SUGGESTION_TAPPED, {
      suggestion_key: suggestionKey,
      suggestion_text: suggestionText,
    });

    // Special handling for "show menu" suggestion
    if (suggestionKey === 'chat.suggestions.show_menu' && menu?.Category) {
      handleShowMenu();
    } else {
      handleSendMessage(suggestionText);
    }
  };

  const handleShowMenu = () => {
    if (!menu?.Category?.Categories) {
      addErrorMessage(tr('chat.error.menu_not_found'));
      return;
    }

    // Build menu message from subcategories
    const rootCategory = menu.Category;
    const subCategories = rootCategory.Categories || [];

    if (subCategories.length === 0) {
      handleSendMessage(tr('chat.special.no_categories'));
      return;
    }

    let menuMessage = tr('chat.categories.intro');

    subCategories.forEach((cat) => {
      const productCount = cat.Products?.length || 0;
      const serviceCount = cat.Services?.length || 0;
      const itemCount = productCount + serviceCount;

      menuMessage += `\n\n**${cat.name}**`;
      if (cat.description) {
        menuMessage += `\n${cat.description}`;
      }
      menuMessage += `\n(${itemCount} ${itemCount === 1 ? 'item' : 'items'})`;
    });

    // Send the menu as a user message
    const userMessage = {
      role: 'user',
      content: tr('chat.suggestions.show_menu'),
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, userMessage]);

    // Add assistant message with the menu
    const assistantMessage = {
      role: 'assistant',
      content: menuMessage,
      timestamp: new Date(),
      categories: subCategories,
    };
    setMessages((prev) => [...prev, assistantMessage]);

    // Track menu shown
    analytics.trackEvent('menu_shown', {
      category_count: subCategories.length,
    });
  };

  const handleFollowUpSelect = async (option) => {
    analytics.trackEvent(ANALYTICS_EVENTS.MESSAGE_FOLLOWUP_TAPPED, {
      option_name: option.name,
      option_type: followUp?.prompt_type,
    });
    // Send the selection as a message
    handleSendMessage(option.name || option);
  };

  const addErrorMessage = (message) => {
    setMessages((prev) => [
      ...prev,
      {
        role: 'assistant',
        content: message,
        timestamp: new Date(),
        isError: true,
      },
    ]);
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleClose = () => {
    // Track chat closed
    if (sessionStartTime) {
      const duration = (new Date() - sessionStartTime) / 1000;
      analytics.trackChatClosed(duration, messageCount);
    }
    setIsOpen(false);
  };

  const findProductById = (productId, products) => {
    if (!products) return null;
    return products.find((p) => p.id === productId);
  };

  if (!isOpen) return null;

  return (
    <div className={inLanding ? "landing-chat-widget centered" : "chat-widget"}>
      <ChatHeader onClose={handleClose} />

      <div className="chat-messages">
        {messages.map((message, index) => (
          <ChatMessage key={index} message={message} />
        ))}

        {isLoading && (
          <div className="typing-indicator">
            <span></span>
            <span></span>
            <span></span>
          </div>
        )}

        {followUp && (
          <div className="chat-follow-up">
            <p>{followUp.prompt}</p>
            {followUp.options && followUp.options.length > 0 && (
              <div className="follow-up-options">
                {followUp.options.map((option, index) => (
                  <button
                    key={index}
                    className="follow-up-option-btn"
                    onClick={() => handleFollowUpSelect(option)}
                    disabled={isLoading}
                  >
                    {option.name}
                    {option.price !== undefined && ` (+$${option.price.toFixed(2)})`}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {!isLoading && suggestions.length > 0 && !followUp && (
          <ChatSuggestions
            suggestions={suggestions}
            onSuggestionClick={handleSuggestionClick}
          />
        )}

        <div ref={messagesEndRef} />
      </div>

      <ChatInput
        onSendMessage={handleSendMessage}
        disabled={isLoading}
        ref={inputRef}
      />
    </div>
  );
}

export default ChatWidget;
